/* The camera dialog, shared by every product input on the page. */

import { byId } from "./dom";
import { describeCameraError, scanFromCamera, type Scan } from "./scanner";

let activeScan: Scan | null = null;

export function closeScanner(): void {
  activeScan?.stop();
  activeScan = null;
  const dialog = byId<HTMLDialogElement>("scanDialog");
  if (dialog.open) dialog.close();
}

/** Open the camera and resolve with a barcode, or null when cancelled. */
export async function scanBarcode(title: string): Promise<string | null> {
  const dialog = byId<HTMLDialogElement>("scanDialog");
  const video = byId<HTMLVideoElement>("scanVideo");
  const status = byId("scanStatus");
  byId("scanTitle").textContent = title;
  status.textContent = "Starting the camera…";
  dialog.showModal();

  const scan = scanFromCamera(video);
  activeScan = scan;
  status.textContent = "Line the bars up across the frame, about 15 cm away.";

  // Torch button, only on cameras that have one.
  const torch = byId<HTMLButtonElement>("scanTorch");
  torch.hidden = true;
  torch.setAttribute("aria-pressed", "false");
  void scan.hasTorch.then((has) => {
    if (activeScan === scan) torch.hidden = !has;
  });
  torch.onclick = () => {
    const on = torch.getAttribute("aria-pressed") !== "true";
    torch.setAttribute("aria-pressed", String(on));
    scan.setTorch(on);
  };

  // Still nothing after a while: the usual causes are distance, blur and glare.
  const tip = window.setTimeout(() => {
    if (activeScan === scan) {
      status.textContent =
        "Not reading yet? Move a little further back so the barcode is in focus, keep it flat and avoid glare. Use Light in dim places.";
    }
  }, 7000);

  try {
    const code = await scan.result;
    if (activeScan !== scan) return null; // cancelled meanwhile
    navigator.vibrate?.(60);
    closeScanner();
    return code;
  } catch (error) {
    if (activeScan !== scan) return null;
    status.textContent = describeCameraError(error);
    scan.stop();
    return null;
  } finally {
    window.clearTimeout(tip);
  }
}

export function wireScannerDialog(): void {
  byId("scanCancel").addEventListener("click", closeScanner);
  // Esc closes a <dialog> natively; make sure the camera stops too.
  byId<HTMLDialogElement>("scanDialog").addEventListener("close", () => {
    activeScan?.stop();
    activeScan = null;
  });
}
